<template lang="pug">
  Modal(:header="email ? __('Share by Email') : __('Share URL')" @close="emit('close')")
    .email(v-if="email" ref="root")
      .email-to
        .help {{__('Enter up to five email addresses, separated by commas')}}
        RogueInput(label="Recipient Email Address" :required="true" v-model="sendTo" :validate-fn="validateRecipients")
      Message(v-if="sendStatus === 'invalid'" status="error")
        Icon.left-icon(name="alert-error")
        | {{__('Recipient field must only include valid email addresses, separated by commas.')}}
      Message(v-if="sendStatus === 'fail'" status="error")
        Icon.left-icon(name="alert-error")
        | {{__('There was an error sending your email. Please try again later.')}}
      Message(v-if="sendStatus === 'success'" status="success") {{__('Your email has been sent.')}}
      RogueButton(
        width="full"
        color="black"
        v-if="sendStatus !== 'success'"
        :loading="sendStatus === 'loading'"
        @click="sendEmail()"
      ) {{ list.type === 'registry' ? __('Share Registry') : __('Share Wishlist')}}
      RogueButton(width="full" @click="emit('close')") {{sendStatus === 'success' ? __('Close') : __('Cancel')}}
    .public-url(v-if="url" @click="copy()" :class="{'large-url': !email}")
      .note {{__('Public URL (click to copy):')}}
        span.copied(:class="{show: copied}") {{__('Copied!')}}
      .url(ref="urlEl") {{url}}
    RogueButton(width="full" v-if="!email" @click="emit('close')") {{__('Close')}}
</template>
<script setup lang="ts">
import { ref } from 'vue'
import { validateEmail as isValidEmail } from '@rogue/universal'
import { validate } from '@/js/helpers/validate-fns'
import { useRogue } from '@/js/services/Rogue'
import type { List } from '@/js/stores/listTypes'

type SendStatus = 'loading' | 'invalid' | 'fail' | 'success' | null

const props = withDefaults(defineProps<{
  list: List,
  email?: boolean,
  url?: string,
}>(), {
  email : true,
  url   : '',
})

const emit = defineEmits<{ close: [] }>()

const rogue = useRogue()

const root = ref<HTMLElement | null>(null)
const urlEl = ref<HTMLElement | null>(null)

const sendTo = ref('')
const sendStatus = ref<SendStatus>(null)
const copied = ref(false)

function validateRecipients(val: string): string | undefined {
  const emails = val.split(',').map(m => m.trim().toLowerCase())
  if (emails.length === 0) {
    return 'Please enter at least one recipient.'
  }

  if (emails.some(e => !isValidEmail(e))) {
    return 'Please ensure that all emails are valid email addresses.'
  }

  if (emails.length > 5) {
    return 'Please enter a maximum of five recipients.'
  }

  return undefined
}

async function sendEmail() {
  if (!root.value || !validate(root.value)) {
    return
  }

  const emails = sendTo.value.split(',').map(m => m.trim().toLowerCase())

  sendStatus.value = 'loading'
  try {
    await rogue.fetch(`/svc_list/lists/${props.list._id}/share`, 'post', { recipients: emails })
    sendStatus.value = 'success'
  } catch (e) {
    sendStatus.value = 'fail'
  }
}

async function copy() {
  const shareResult = await rogue.share({ url: props.url, title: `Share ${props.list.type}` }, { el: urlEl.value })
  if (shareResult === 'clipboard') {
    copied.value = true
    setTimeout(() => copied.value = false, 3000)
  }
}
</script>
<style lang="scss" scoped>

.help {
  font-size: $size-xxs;
  color: $color-gray-x-dark;
  margin-bottom: $space-xxs;
}

.button, .message {
  margin-bottom: $space-s;
}

.captcha {
  margin-bottom: $space-m;
}

.public-url {
  cursor: pointer;
  font-size: $size-xxs;
  color: $color-black-light;

  &.large-url {
    padding: $space-s 0;
    font-size: $size-s;
  }

  .url {
    overflow: hidden;
    margin-top: $space-xxs;
    color: $color-black-light;
    font-size: $size-xxs;
  }
}

.copied {
  display: inline-block;
  margin-left: $space-s;
  color: $color-green-dark;
  opacity: 0;
  transition: opacity 0.2s;

  &.show {
    opacity: 1;
  }
}

</style>
